import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, Input, Typography, Row, Col, Tag, Timeline, Spin, Select, Empty, Space, List, Avatar } from 'antd';
import { SoundOutlined, ClockCircleOutlined, CheckCircleOutlined, FileProtectOutlined, PictureOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../utils/api';

const { Title, Text } = Typography;
const { Search } = Input;

export default function SongTimeline() {
  const [songs, setSongs] = useState([]);
  const [selectedSong, setSelectedSong] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchText, setSearchText] = useState('');

  const debounceRef = useRef(null);

  const searchSongs = useCallback(async (val) => {
    if (!val || val.length < 2) { setSongs([]); return; }
    setSearchLoading(true);
    try {
      const res = await api.get('/songs', { params: { search: val, limit: 20 } });
      setSongs(Array.isArray(res.data) ? res.data : (res.data?.songs || []));
    } catch (err) { console.error(err); }
    setSearchLoading(false);
  }, []);

  const handleTyping = (e) => {
    const val = e.target.value;
    setSearchText(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchSongs(val), 300);
  };

  const loadTimeline = async (song) => {
    setSelectedSong(song);
    setLoading(true);
    try {
      // Get activity log for this song
      const res = await api.get('/activity-log', {
        params: { entity_type: 'song', limit: 100 }
      });
      const allLogs = res.data?.logs || [];
      const songTitle = (song.title || '').toLowerCase().trim();

      // Filter logs related to this song
      const songLogs = allLogs.filter(log => {
        if (log.entity_id === song.id) return true;
        if (log.details) {
          const d = log.details.toLowerCase();
          if (d.includes(songTitle)) return true;
        }
        return false;
      });

      // Build timeline events from song data + logs
      const events = [];

      // Song created
      if (song.created_at) {
        events.push({
          time: song.created_at,
          icon: <SoundOutlined />,
          color: 'green',
          title: 'Song Added to Library',
          desc: `Title: ${song.title}`
        });
      }

      // Audio uploaded
      if (song.audio_path) {
        events.push({
          time: song.created_at || song.upload_date,
          icon: <SoundOutlined />,
          color: 'blue',
          title: 'Audio Uploaded',
          desc: song.audio_path
        });
      }

      // Poster attached
      if (song.poster_path) {
        events.push({
          time: song.created_at,
          icon: <PictureOutlined />,
          color: 'purple',
          title: 'Poster Attached',
          desc: 'Distribution thumbnail matched'
        });
      }

      // Distribution status
      if (song.distribution_status === 'distributed') {
        events.push({
          time: song.distribution_date || song.updated_at || song.created_at,
          icon: <CheckCircleOutlined />,
          color: 'green',
          title: 'Distributed',
          desc: song.distribution_company ? `via ${song.distribution_company}` : 'Marked as distributed'
        });
      }

      // IPRS status
      if (song.iprs_status === 'claimed') {
        events.push({
          time: song.iprs_date || song.updated_at,
          icon: <FileProtectOutlined />,
          color: 'gold',
          title: 'IPRS Claimed',
          desc: 'IPRS claim submitted'
        });
      }

      // Agreement
      if (song.agreement_path) {
        events.push({
          time: song.updated_at,
          icon: <FileProtectOutlined />,
          color: 'cyan',
          title: 'Agreement Uploaded',
          desc: 'Song agreement document attached'
        });
      }

      // Add activity log events
      songLogs.forEach(log => {
        events.push({
          time: log.created_at,
          icon: <ClockCircleOutlined />,
          color: 'blue',
          title: log.action.replace(/_/g, ' '),
          desc: `By ${log.full_name || log.username}${log.details ? ' - ' + log.details : ''}`
        });
      });

      // Sort by time
      events.sort((a, b) => new Date(a.time) - new Date(b.time));

      setTimeline(events);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  return (
    <div>
      <Title level={3}><ClockCircleOutlined /> Song Timeline</Title>
      <Text type="secondary">View the complete lifecycle of any song</Text>

      <Card style={{ marginTop: 16, marginBottom: 16 }}>
        <Search
          placeholder="Type song title... (auto-search)"
          enterButton={<SearchOutlined />}
          size="large"
          loading={searchLoading}
          onSearch={searchSongs}
          onChange={handleTyping}
          value={searchText}
        />
      </Card>

      <Row gutter={16}>
        <Col xs={24} md={8}>
          <Card title="Search Results" size="small" style={{ maxHeight: 600, overflow: 'auto' }}>
            {songs.length === 0 ? (
              <Empty description="Search for a song to see its timeline" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              <List
                size="small"
                dataSource={songs}
                renderItem={song => (
                  <List.Item
                    style={{ cursor: 'pointer', background: selectedSong?.id === song.id ? '#e6f7ff' : 'transparent', padding: '8px 12px', borderRadius: 8 }}
                    onClick={() => loadTimeline(song)}
                  >
                    <List.Item.Meta
                      avatar={song.poster_path ? <Avatar src={song.poster_path} shape="square" /> : <Avatar icon={<SoundOutlined />} style={{ background: '#667eea' }} />}
                      title={<Text strong style={{ fontSize: 13 }}>{song.title}</Text>}
                      description={<Space size={4}>{song.singer && <Tag>{song.singer}</Tag>}{song.distribution_status === 'distributed' ? <Tag color="green">Distributed</Tag> : <Tag color="orange">Pending</Tag>}</Space>}
                    />
                  </List.Item>
                )}
              />
            )}
          </Card>
        </Col>

        <Col xs={24} md={16}>
          <Card title={selectedSong ? `Timeline: ${selectedSong.title}` : 'Song Timeline'} size="small">
            {loading ? (
              <Spin size="large" style={{ display: 'block', textAlign: 'center', padding: 40 }} />
            ) : !selectedSong ? (
              <Empty description="Select a song to view its timeline" />
            ) : timeline.length === 0 ? (
              <Empty description="No timeline events found" />
            ) : (
              <>
                {/* Song info card */}
                <Card size="small" style={{ marginBottom: 16, background: '#fafafa' }}>
                  <Row gutter={16}>
                    <Col span={6}><Text type="secondary">Singer:</Text><br /><Text strong>{selectedSong.singer || '-'}</Text></Col>
                    <Col span={6}><Text type="secondary">Studio:</Text><br /><Text strong>{selectedSong.studio || '-'}</Text></Col>
                    <Col span={6}><Text type="secondary">Status:</Text><br />{selectedSong.distribution_status === 'distributed' ? <Tag color="green">Distributed</Tag> : <Tag color="orange">Pending</Tag>}</Col>
                    <Col span={6}><Text type="secondary">IPRS:</Text><br />{selectedSong.iprs_status === 'claimed' ? <Tag color="gold">Claimed</Tag> : <Tag>Pending</Tag>}</Col>
                  </Row>
                </Card>

                <Timeline>
                  {timeline.map((ev, i) => (
                    <Timeline.Item key={i} color={ev.color} dot={ev.icon}>
                      <p style={{ margin: 0 }}>
                        <Text strong>{ev.title}</Text>
                        <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>{dayjs(ev.time).format('DD MMM YYYY, hh:mm A')}</Text>
                      </p>
                      {ev.desc && <Text type="secondary" style={{ fontSize: 12 }}>{ev.desc}</Text>}
                    </Timeline.Item>
                  ))}
                </Timeline>
              </>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
}
